import React from "react";
import type { WorkbenchTierProgress } from "@/types/workbench";

type Props = {
  done: number;
  total: number;
  byTier: WorkbenchTierProgress[];
  reportDate?: string;
};

/** Top strip: a progress ring + a plain-language lead + per-tier counts. */
export function ProgressStrip({ done, total, byTier, reportDate }: Props) {
  // Stable, simple computation: done/total → percentage
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const r = 20;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - (total > 0 ? done / total : 0));

  const remaining = Math.max(0, total - done);
  const lead =
    total === 0
      ? "No fixes in your queue"
      : remaining === 0
        ? "All fixes done — nice work"
        : `${remaining} fix${remaining === 1 ? "" : "es"} left`;
  const sub =
    total === 0
      ? "This report has no open AI Trust Signal fixes."
      : `${done} of ${total} done${reportDate ? ` · from your ${formatMonth(reportDate)} report` : ""}`;

  return (
    <div className="wb-progress">
      <div className="wb-ring-wrap">
        <svg className="wb-ring" viewBox="0 0 48 48" aria-hidden="true">
          <circle className="wb-ring__track" cx="24" cy="24" r={r} fill="none" strokeWidth="4" />
          <circle
            className="wb-ring__fill"
            cx="24"
            cy="24"
            r={r}
            fill="none"
            strokeWidth="4"
            strokeDasharray={circ}
            strokeDashoffset={offset}
          />
        </svg>
        <span className="wb-ring__pct">{pct}%</span>
      </div>
      <div className="wb-progress__txt">
        <span className="wb-progress__lead">{lead}</span>
        <span className="wb-progress__sub">{sub}</span>
      </div>
      {byTier.length > 0 && (
        <div className="wb-progress__tiers" aria-label="Progress by tier">
          {byTier.map((t) => (
            <span key={t.tier} className="wb-tier-pill" title={`${t.label}: ${t.done} of ${t.total} done`}>
              {t.label}
              <span className="wb-tier-pill__n tnum">
                {t.done}/{t.total}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "latest";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default ProgressStrip;
